/**
 * Iter 203.3 — Capa de transporte de Drops (abstracción intercambiable).
 *
 * Implementación actual: POLLING contextual y eficiente.
 *   · UN solo loop global — jamás múltiples timers simultáneos.
 *   · Solo consulta cuando hay un Post/Reel realmente visible registrado
 *     (Feed/Reels), la pestaña está visible y hay sesión autenticada.
 *   · Intervalo 20–30s con jitter aleatorio + backoff exponencial ante
 *     errores de red (hasta 5 min).
 *   · Se detiene por completo en background, al salir de Feed/Reels y
 *     durante el cooldown post-drop.
 *   · El drop SOLO se anima cuando el backend lo autoriza (drop_event).
 *
 * Interfaz de transporte (misma para un futuro WebSocket/SSE):
 *   registerContext(type, id) / unregisterContext(type, id)
 *   subscribe(fn) → unsubscribe
 *   resolveDrop() — el consumidor avisa que el drop terminó (catch o miss)
 */
import { dropsApi } from '@/lib/dropsApi';

const MIN_INTERVAL_MS = 20000;
const MAX_INTERVAL_MS = 30000;
const FIRST_CHECK_DELAY_MS = 3500;
const MAX_BACKOFF_MS = 5 * 60 * 1000;
const POST_DROP_COOLDOWN_MS = 10 * 60 * 1000;
const FLUSH_EVERY_CHECKS = 8;

class PollingDropTransport {
  constructor() {
    this.contexts = new Map();
    this.listeners = new Set();
    this.timer = null;
    this.inFlight = false;
    this.errorStreak = 0;
    this.cooldownUntil = 0;
    this.activeDrop = null;
    this.firstCheckDone = false;
    this.sessionReported = false;
    this.sinceFlush = 0;
    this.metrics = { requests: 0, latency_ms_sum: 0, latency_count: 0, errors: 0 };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this._schedule();
        else { this._clearTimer(); this.flush(); }
      });
      window.addEventListener('pagehide', () => this.flush());
    }
  }

  registerContext(type, id) {
    this.contexts.set(`${type}:${id}`, { type, id, at: Date.now() });
    this._schedule();
  }

  unregisterContext(type, id) {
    this.contexts.delete(`${type}:${id}`);
    if (!this.contexts.size) this._clearTimer();
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** El consumidor (DropLayer) avisa que el drop actual terminó. */
  resolveDrop() {
    this.activeDrop = null;
    this.cooldownUntil = Date.now() + POST_DROP_COOLDOWN_MS;
    this.flush();
    this._schedule();
  }

  _current() {
    let cur = null;
    this.contexts.forEach((c) => { if (!cur || c.at > cur.at) cur = c; });
    return cur;
  }

  _clearTimer() {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
  }

  _delay() {
    if (!this.firstCheckDone && !this.errorStreak) return FIRST_CHECK_DELAY_MS;
    const base = MIN_INTERVAL_MS
      + Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS);
    if (!this.errorStreak) return base;
    return Math.min(base * (2 ** this.errorStreak), MAX_BACKOFF_MS);
  }

  _schedule() {
    if (this.timer || this.activeDrop || !this.contexts.size) return;
    if (typeof document !== 'undefined'
        && document.visibilityState !== 'visible') return;
    this.timer = setTimeout(() => { this.timer = null; this._tick(); },
      this._delay());
  }

  async _tick() {
    if (this.inFlight || this.activeDrop) return;
    const ctx = this._current();
    // Auth es por cookies httpOnly — el backend responde 401 si no hay
    // sesión y el backoff exponencial evita spam. Sensores solo montan
    // en rutas protegidas, así que en la práctica siempre hay sesión.
    if (!ctx || Date.now() < this.cooldownUntil) {
      this._schedule();
      return;
    }
    this.inFlight = true;
    const t0 = performance.now();
    try {
      const res = await dropsApi.next(ctx.type, ctx.id);
      this.firstCheckDone = true;
      this.errorStreak = 0;
      this.metrics.requests += 1;
      this.metrics.latency_ms_sum += Math.round(performance.now() - t0);
      this.metrics.latency_count += 1;
      if (res?.drop) {
        this.activeDrop = res.drop;
        this.listeners.forEach((fn) => {
          try { fn(res.drop); } catch { /* listener error */ }
        });
      }
    } catch {
      this.errorStreak = Math.min(this.errorStreak + 1, 5);
      this.metrics.errors += 1;
    } finally {
      this.inFlight = false;
      this.sinceFlush += 1;
      if (this.sinceFlush >= FLUSH_EVERY_CHECKS) this.flush();
      this._schedule();
    }
  }

  flush() {
    const m = this.metrics;
    if (!m.requests && !m.errors) return;
    const body = { ...m, new_session: !this.sessionReported };
    this.sessionReported = true;
    this.metrics = { requests: 0, latency_ms_sum: 0, latency_count: 0, errors: 0 };
    this.sinceFlush = 0;
    dropsApi.metrics(body).catch(() => { /* beacon best-effort */ });
  }
}

export const dropTransport = new PollingDropTransport();
